import * as Sentry from '@sentry/react';

import { client } from './client';
import { GET_ROUTE_SEGMENT_EXTERNAL_WORKS } from './queries/surveillanceRoute';
import type {
  ExternalWorksBySegment,
  LinkedWorkRef,
} from '../types/surveillanceRoute';

/**
 * Trabajos que atendieron el aviso de cada tramo, por id de tramo.
 *
 * 🔴 NUNCA rechaza: sin red, o con el back fallando al calcular, devuelve `null`
 * («no se sabe»), que NO es `{}` («el servidor dice que no hay ninguno»): la
 * pantalla conserva lo último bueno en vez de borrar las pills, y el informe
 * sale como antes (solo la OT). `no-cache`: ni lee ni escribe la caché de
 * Apollo, así no puede servir un dato viejo ni ensuciar los tramos.
 */
export async function fetchExternalWorksBySegment(
  routeId: string
): Promise<ExternalWorksBySegment | null> {
  try {
    const response = await client.query({
      query: GET_ROUTE_SEGMENT_EXTERNAL_WORKS,
      variables: { id: routeId },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
    // Errores del back al calcular (no de red): se avisan, y la pantalla sigue
    // como antes. Un error de red cae en el `catch` y NO se reporta (normal en tablet).
    const responseErrors = (response as { errors?: readonly unknown[] })?.errors;
    if (responseErrors && responseErrors.length > 0) {
      Sentry.captureMessage('linkedExternalWorks: el back devolvió errores', {
        level: 'warning',
        tags: { feature: 'surveillance-route', component: 'external-works-fetch' },
        extra: { routeId, errors: responseErrors },
      });
    }
    const segments = response?.data?.routeSegments as
      | Array<{ id: string; linkedExternalWorks?: LinkedWorkRef[] | null }>
      | null
      | undefined;
    // Sin la lista de tramos el servidor no ha dicho qué trabajos hay.
    if (!Array.isArray(segments)) return null;
    return Object.fromEntries(
      segments.map((segment) => [segment.id, segment.linkedExternalWorks ?? []])
    );
  } catch {
    return null;
  }
}
